import React, { createContext, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useSelector, useDispatch } from "react-redux";
import { handleIncomingMessage, handleMessageEdit, handleMessageRemove, handleOnlineStatus, handleReadMessageCallback, handleReceiverReadMessage } from "store/actions/clientMessaging";
import { WEBSOCKET_CONNECTION_URL } from "store/services/apiPath";
import chimeSound from "Assets/sounds/notification-chime.mp3";
import { getConversationsList, initiateConversation } from "store/services/clientMessagingService";
import ToastNotification from 'components/shared/ToastNotification';
import { Slide, toast } from 'react-toastify';
import { useLocation } from 'react-router-dom';
import { FULL_NAME } from 'helpers/common';

const WebSocketContext = createContext(null);
export const useWebSocket = () => useContext(WebSocketContext);

export const WebSocketProvider = ({ children }) => {
  const dispatch = useDispatch();
  const userInfo = useSelector((state) => state.Auth.user);
  const practSettings = useSelector((state) => state.Auth.practitionerSettings);
  const accountSettings = useSelector((state) => state.AccountSetting.accountSettingsFlat);
  const conversation = useSelector((state) => state.ClientMessaging.conversation);
  const [isConnected, setIsConnected] = useState(false);
  const [documentHidden, setDocumentHidden] = useState(false);
  const [isChatEnabled, setChatEnabled] = useState(true);

  const socketRef = useRef(null);
  const chimeRef = useRef(null);
  const pendingAcks = useRef({});
  const reconnectTimeoutRef = useRef(null);
  const reconnectAttemptsRef = useRef(0);
  const pingIntervalRef = useRef(null);
  const conversationRef = useRef(conversation);

  const audioChimeEnabledRef = useRef(false);

  const location = useLocation();
  const locationRef = useRef(location);

  useEffect(() => {
    locationRef.current = location;
  }, [location]);

  useEffect(() => {
    conversationRef.current = conversation;
  }, [conversation]);

  useEffect(() => {
    audioChimeEnabledRef.current = accountSettings?.new_chat_msg_notification === "enable";
  }, [accountSettings]);

  const unreadMessagesCount = useMemo(() => {
    return conversation?.unread_count || 0;
  }, [conversation]);

  useEffect(() => {
    if(!userInfo?.token || !practSettings?.messaging_status) {
      return;
    }

    if (practSettings?.messaging_status === "enable") {
      dispatch(initiateConversation());
      connectSocket();
      setChatEnabled(true);
    } else {
      setChatEnabled(false);
    }

    return () => {
      socketRef.current?.close();
      clearTimeout(reconnectTimeoutRef.current);
      // clearInterval(pingIntervalRef.current);
    };
  }, [userInfo?.token, practSettings?.messaging_status]);

  useEffect(() => {
    chimeRef.current = new Audio(chimeSound);
    chimeRef.current.load();
  }, []);

  // useEffect(() => {
  //   if (userInfo?.token && practSettings?.messaging_status === "enable") {
  //     dispatch(initiateConversation());
  //   }
  // }, [userInfo?.token, practSettings?.messaging_status]);


  useEffect(() => {
    const handleVisibilityChange = () => {
      setDocumentHidden(document.hidden);
    };
    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => document.removeEventListener("visibilitychange", handleVisibilityChange);
  }, []);

  useEffect(() => {
    document.title = documentHidden && unreadMessagesCount > 0
      ? `(${unreadMessagesCount}) New Message`
      : "Nutri-Q";
  }, [unreadMessagesCount, documentHidden]);


  const notify = async (data) => {
    if (locationRef.current.pathname === "/chat") return;

    if(!conversationRef?.current) return;

    const practName = await FULL_NAME(conversationRef?.current?.pract_first_name, conversationRef?.current?.pract_last_name);

    let parsedMessage;
    try {
      parsedMessage = JSON.parse(data.message);
    } catch (error) {
      console.error("Failed to parse message JSON:", error);
      return;
    }
    parsedMessage = parsedMessage.content[0].content[0].text
    toast(
      <ToastNotification
        name={practName}
        message={parsedMessage}
        icon="/icon.png"
      />,
      {
        position: "top-right",
        autoClose: true,
        hideProgressBar: true,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        theme: "light",
        transition: Slide,
        className: ' border border-purple-600/40 border-r rounded-3',
      }
    );
  }


  const connectSocket = () => {
    if (socketRef.current) {
      socketRef.current.close();
    }

    const wsUrl = `${WEBSOCKET_CONNECTION_URL}?token=${userInfo?.token}`;
    socketRef.current = new WebSocket(wsUrl);

    socketRef.current.onopen = () => {
      console.log('✅ WebSocket connected');
      setIsConnected(true);
      reconnectAttemptsRef.current = 0;

      // Start ping interval to keep socket alive
      // pingIntervalRef.current = setInterval(() => {
      //   if (socketRef.current?.readyState === WebSocket.OPEN) {
      //     socketRef.current.send(JSON.stringify({ type: "ping" }));
      //   }
      // }, 30000); // Ping every 30 seconds
    };

    socketRef.current.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);

        if (data.action === 'messageAck' && data.idempotencyId) {
          const callbackEntry = pendingAcks.current[data.idempotencyId];
          if (callbackEntry) {
            callbackEntry.onSuccess?.(data);
            delete pendingAcks.current[data.idempotencyId];
          }
        } else if (data.action === "msgRead") {
          dispatch(handleReadMessageCallback(data));
        } else if (data.action === "sentMsgReadByReceiver") {
          dispatch(handleReceiverReadMessage(data));
        } else if (data.action === "editMessage") {
          dispatch(handleMessageEdit(data));
        } else if (data.action === "removeMessage") {
          dispatch(handleMessageRemove(data));
        } else if (data.action === "active_status") {
          dispatch(handleOnlineStatus(data));
        } else {
          dispatch(handleIncomingMessage(data));

          notify(data);

          // Play chime for a new message
          if (document.hidden && audioChimeEnabledRef.current && chimeRef.current) {
            chimeRef.current.play().catch(err => {
              console.warn("Chime play failed:", err);
            });
          }
        }
      } catch (err) {
        console.error("Error in incoming message handler", err);
      }
    };

    socketRef.current.onerror = (error) => {
      console.error('❌ WebSocket error:', error);
    };

    socketRef.current.onclose = (event) => {
      console.log(`🔌 WebSocket closed: code=${event.code}, reason=${event.reason}`);
      setIsConnected(false);
      // clearInterval(pingIntervalRef.current); // Clear ping on close

      // Only reconnect on unexpected closure
      const shouldReconnect = [1006, 1011, 1001].includes(event.code); // 1006: abnormal, 1011: internal error, 1001: going away

      if (shouldReconnect && reconnectAttemptsRef.current < 5) {
        const timeout = Math.min(1000 * 2 ** reconnectAttemptsRef.current, 10000);
        console.log(`⏳ Attempting reconnect #${reconnectAttemptsRef.current + 1} in ${timeout / 1000}s...`);
        reconnectTimeoutRef.current = setTimeout(connectSocket, timeout);
        reconnectAttemptsRef.current += 1;
      } else {
        console.log("Not reconnecting, either clean close or retry limit exceeded");
      }
    };
  };

  const sendMessage = (message, onSuccess, onError) => {
    const idempotencyId = message.idempotencyId;

    if (onSuccess || onError) {
      pendingAcks.current[idempotencyId] = { onSuccess, onError };
    }

    if (socketRef.current?.readyState === WebSocket.OPEN) {
      socketRef.current.send(JSON.stringify(message));
    } else {
      console.warn('WebSocket not open');
      if (onError) onError('Socket not open');
    }
  };

  return (
    <WebSocketContext.Provider value={{ sendMessage, isConnected, unreadMessagesCount, isChatEnabled }}>
      {children}
    </WebSocketContext.Provider>
  );
};
